/**
 * ExecutionRenderer — renders test execution results in the detail panel.
 * Shows step-by-step pass/fail with screenshots for each step.
 * Displays a running animation for queued/running executions.
 */

import { CheckCircle, XCircle, Clock, Globe, ExternalLink, Loader2, AlertTriangle, SkipForward } from 'lucide-react'

import { useTestOpsCommandConfig } from '../../context'

interface ExecutionStep {
  step_number: number
  action: string
  success: boolean
  skipped?: boolean
  step_type?: string
  error_category?: string
  reasoning?: string
  error?: string
  duration_ms?: number
  screenshot_url?: string
}

function getErrorLabel(category: string | undefined): string {
  if (!category) return 'Failed'
  const labels: Record<string, string> = {
    element_not_found: 'Element Not Found',
    element_not_interactable: 'Element Not Interactable',
    assertion_failed: 'Assertion Failed',
    action_failed: 'Action Failed',
    step_timeout: 'Timeout',
    navigation_timeout: 'Navigation Timeout',
    navigation_failed: 'Navigation Failed',
    network_error: 'Network Error',
    partial_failure: 'Partial Failure',
  }
  return labels[category] || category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled'])

export function isExecutionTerminal(status: string): boolean {
  return TERMINAL_STATUSES.has(status)
}

export function ExecutionRenderer({ data }: { data: Record<string, unknown> }) {
  const { ScreenshotComponent: ScreenshotImage } = useTestOpsCommandConfig()
  const status = (data.status as string) || ''
  const targetUrl = (data.target_url as string) || ''
  const duration = data.duration_seconds as number | undefined
  const result = (data.result as string) || ''
  const error = (data.error as string) || ''
  const steps = (data.steps as ExecutionStep[]) || []
  const historyUrl = (data.history_url as string) || ''
  const stepCount = (data.step_count as number) || steps.length
  const testCaseName = (data.test_case_name as string) || ''

  const passedSteps = steps.filter(s => s.success && !s.skipped).length
  const failedSteps = steps.filter(s => !s.success && !s.skipped).length
  const skippedSteps = steps.filter(s => s.skipped).length
  const isRunning = !isExecutionTerminal(status)

  return (
    <div className="space-y-4">
      {/* Running animation banner */}
      {isRunning && (
        <div className="rounded-lg border border-accent/30 bg-accent-soft p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="relative">
              <Loader2 className="w-6 h-6 text-accent animate-spin" />
              <div className="absolute inset-0 w-6 h-6 rounded-full border-2 border-accent/30 animate-ping opacity-20" />
            </div>
            <div>
              <p className="font-semibold text-accent">
                {status === 'queued' ? 'Preparing Execution' : 'Executing Test'}
              </p>
              {testCaseName && (
                <p className="text-accent mt-0.5">{testCaseName}</p>
              )}
            </div>
          </div>
          {/* Progress dots animation */}
          <div className="flex items-center gap-1.5 mt-2">
            <div className="w-2 h-2 rounded-full bg-accent animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 rounded-full bg-accent animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 rounded-full bg-accent animate-bounce" style={{ animationDelay: '300ms' }} />
            <span className="ml-2 text-accent">
              {status === 'queued'
                ? 'Waiting for available browser session...'
                : 'Browser automation in progress...'}
            </span>
          </div>
        </div>
      )}

      {/* Status banner — only for terminal statuses */}
      {!isRunning && (
        <div className={`rounded-lg p-2.5 flex items-center justify-between ${
          status === 'completed' ? 'bg-success-soft border border-success/30'
          : status === 'failed' ? 'bg-destructive-soft border border-destructive/30'
          : 'bg-foreground/[0.04] border border-subtle'
        }`}>
          <div className="flex items-center gap-2">
            {status === 'completed' ? <CheckCircle className="w-4 h-4 text-success-foreground" />
             : status === 'failed' ? <XCircle className="w-4 h-4 text-destructive-foreground" />
             : <Clock className="w-4 h-4 text-muted-foreground" />}
            <span className={`font-semibold ${
              status === 'completed' ? 'text-success-foreground'
              : status === 'failed' ? 'text-destructive-foreground'
              : 'text-foreground'
            }`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
            {stepCount > 0 && (
              <span className="text-muted-foreground">
                {passedSteps}/{stepCount} passed
                {failedSteps > 0 && ` · ${failedSteps} failed`}
                {skippedSteps > 0 && ` · ${skippedSteps} skipped`}
              </span>
            )}
          </div>
          {duration != null && (
            <span className="text-muted-foreground">
              <Clock className="w-3 h-3 inline mr-1" />
              {duration.toFixed(1)}s
            </span>
          )}
        </div>
      )}

      {/* Target URL + History link */}
      <div className="flex items-center justify-between gap-2">
        {targetUrl && (
          <div className="flex items-center gap-1.5 text-muted-foreground min-w-0">
            <Globe className="w-3 h-3 shrink-0" />
            <span className="truncate">{targetUrl}</span>
          </div>
        )}
        {historyUrl && (
          <a href={historyUrl} className="flex items-center gap-1 text-accent hover:text-accent-soft-foreground shrink-0">
            <ExternalLink className="w-3 h-3" />
            <span>Full view</span>
          </a>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-destructive-soft border border-destructive/30 p-2.5 text-destructive-foreground">
          {error}
        </div>
      )}

      {/* Result summary */}
      {result && !error && (
        <p className="text-muted-foreground">{result}</p>
      )}

      {/* Steps */}
      {steps.length > 0 && (
        <div className="space-y-2">
          {steps.map((step) => {
            const isFailed = !step.success && !step.skipped
            return (
              <div key={step.step_number} className={`rounded-lg overflow-hidden ${
                step.skipped ? 'border border-subtle'
                : isFailed ? 'border-2 border-destructive/40'
                : 'border border-subtle'
              }`}>
                {/* Step header */}
                <div className={`flex items-start gap-2 px-2.5 py-1.5 ${
                  step.skipped ? 'bg-foreground/[0.04] opacity-60'
                  : isFailed ? 'bg-destructive-soft'
                  : 'bg-foreground/[0.03]'
                }`}>
                  <span className="text-subtle-foreground font-mono w-4 shrink-0 text-right mt-0.5">
                    {step.step_number}.
                  </span>
                  {step.skipped
                    ? <SkipForward className="w-3.5 h-3.5 text-subtle-foreground shrink-0 mt-0.5" />
                    : step.success
                    ? <CheckCircle className="w-3.5 h-3.5 text-success shrink-0 mt-0.5" />
                    : <XCircle className="w-3.5 h-3.5 text-destructive shrink-0 mt-0.5" />
                  }
                  <div className="flex-1 min-w-0">
                    <span className={step.skipped ? 'text-subtle-foreground' : 'text-foreground'}>{step.action}</span>
                    {step.skipped && (
                      <span className="ml-2 text-xs text-subtle-foreground font-medium">Skipped</span>
                    )}
                    {isFailed && step.duration_ms != null && (
                      <span className="ml-2 text-subtle-foreground text-xs">
                        {step.duration_ms >= 1000
                          ? `${(step.duration_ms / 1000).toFixed(1)}s`
                          : `${Math.round(step.duration_ms)}ms`}
                      </span>
                    )}
                    {step.success && !step.skipped && step.duration_ms != null && (
                      <span className="ml-2 text-subtle-foreground text-xs">
                        {step.duration_ms >= 1000
                          ? `${(step.duration_ms / 1000).toFixed(1)}s`
                          : `${Math.round(step.duration_ms)}ms`}
                      </span>
                    )}
                  </div>
                </div>

                {/* Error callout — prominent block for failed steps */}
                {isFailed && (step.error_category || step.error || step.reasoning) && (
                  <div className="border-t border-destructive/30 bg-destructive-soft px-3 py-2">
                    <div className="flex items-center gap-1.5 mb-1">
                      <AlertTriangle className="w-3.5 h-3.5 text-destructive shrink-0" />
                      <span className="text-sm font-semibold text-destructive-foreground">
                        {getErrorLabel(step.error_category)}
                      </span>
                    </div>
                    {step.error && (
                      <p className="text-sm text-destructive-foreground ml-5">{step.error}</p>
                    )}
                    {step.reasoning && !step.error && (
                      <p className="text-sm text-destructive-foreground ml-5">{step.reasoning}</p>
                    )}
                  </div>
                )}

                {/* Step screenshot — not shown for skipped steps */}
                {step.screenshot_url && !step.skipped && (
                  <div className="border-t border-subtle">
                    <ScreenshotImage
                      src={step.screenshot_url}
                      alt={`Step ${step.step_number}: ${step.action}`}
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
